import { supabase } from "@/lib/supabase/client";
import { parseContactCategory } from "@/lib/contact-categories";

export type ContactFormState = {
  success: boolean;
  message: string;
};

export type CancellationFormState = {
  success: boolean;
  message: string;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export async function submitContactForm(formData: FormData): Promise<ContactFormState> {
  try {
    const rawCategoryValue = (formData.get("category") as string) || "";
    const categoryValue = parseContactCategory(rawCategoryValue);
    const firstName = (formData.get("firstName") as string)?.trim() || "";
    const lastName = (formData.get("lastName") as string)?.trim() || "";
    const email = (formData.get("email") as string)?.trim() || "";
    const phone = ((formData.get("phone") as string) || "").trim() || null;
    const message = ((formData.get("message") as string) || "").trim() || null;
    const privacyAccepted = formData.get("privacy") === "yes";

    if (!firstName || !lastName || !email) {
      return { success: false, message: "Bitte Vorname, Nachname und E-Mail ausfüllen." };
    }

    if (!emailPattern.test(email)) {
      return { success: false, message: "Bitte eine gültige E-Mail-Adresse eingeben." };
    }

    if (!privacyAccepted) {
      return { success: false, message: "Bitte stimmen Sie den Datenschutzbestimmungen zu." };
    }

    if (!categoryValue) {
      return { success: false, message: "Bitte eine Kategorie auswählen." };
    }

    const { error } = await supabase.from("contact_inquiries").insert({
      category: categoryValue,
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      message,
      status: "Offen",
    });

    if (error) {
      console.error("Supabase insert error:", error);
      return { success: false, message: "Fehler beim Senden der Anfrage." };
    }

    return { success: true, message: "Deine Anfrage wurde erfolgreich gesendet!" };
  } catch (error) {
    console.error("Error submitting contact form:", error);
    return { success: false, message: "Netzwerkfehler. Bitte versuchen Sie es später erneut." };
  }
}

export async function submitCancellationForm(formData: FormData): Promise<CancellationFormState> {
  try {
    const firstName = (formData.get("firstName") as string)?.trim() || "";
    const lastName = (formData.get("lastName") as string)?.trim() || "";
    const email = ((formData.get("email") as string) || "").trim() || null;
    const phone = ((formData.get("phone") as string) || "").trim() || null;
    const memberNumber = (formData.get("memberNumber") as string)?.trim() || "";
    const reason = ((formData.get("reason") as string) || "").trim() || null;
    const privacyAccepted = formData.get("privacy") === "yes";

    if (!firstName || !lastName) {
      return { success: false, message: "Bitte Vorname und Nachname ausfüllen." };
    }
    if (!memberNumber) {
      return { success: false, message: "Bitte Mitgliedsnummer angeben." };
    }
    if (email && !emailPattern.test(email)) {
      return { success: false, message: "Bitte eine gültige E-Mail-Adresse eingeben." };
    }
    if (!privacyAccepted) {
      return { success: false, message: "Bitte stimme den Datenschutzbestimmungen zu." };
    }

    const messageParts = [`Mitgliedsnummer: ${memberNumber}`];
    if (reason) messageParts.push(`\nGrund: ${reason}`);

    const { error } = await supabase.from("contact_inquiries").insert({
      category: "Kündigung",
      first_name: firstName,
      last_name: lastName,
      email: email ?? "",
      phone,
      message: messageParts.join(""),
      status: "Offen",
    });

    if (error) {
      console.error("Supabase insert error:", error);
      return { success: false, message: "Fehler beim Senden. Bitte versuche es später erneut." };
    }

    return { success: true, message: "Deine Kündigung wurde übermittelt. Wir melden uns in Kürze bei dir." };
  } catch (error) {
    console.error("Error submitting cancellation form:", error);
    return { success: false, message: "Netzwerkfehler. Bitte versuche es später erneut." };
  }
}
